export type UseFormFieldsData = {
  memberValues: Ref<any>;
  setFields: (fields: any[]) => void;
  getValue: (name: string) => any;
  setValue: (name: string, value: any) => void;
  id: Ref<string>;
  edited: Ref<boolean>;
};

enum ValueType {
  Text = 'string',
  Array = 'array',
}

export const useFormFieldsData = (
  step: Ref<any>,
  prefill: boolean = false
): UseFormFieldsData => {
  const memberValues = ref<any>({});
  const initialValues = ref<any>(null);
  const id = ref<string>('');
  const edited = ref<boolean>(!prefill);
  const profile = useProfileStore();
  const route = useRoute();

  const user = useUserStore();

  const getRouteHashParams = () => {
    if (route.hash.indexOf('#/param/') === 0) {
      const param = route.hash.replace('#/param/', '');
      try {
        const data = JSON.parse(atob(param));
        return data;
      } catch (e) {
        return {};
      }
    }
    return {};
  };

  const getDefaultValue = (field, type: ValueType = ValueType.Text) => {
    const routeParams = getRouteHashParams();
    let defaultValue = field.memberValue || (routeParams[field.name] as string);
    if (defaultValue) {
      if (type === ValueType.Array) {
        defaultValue = Array.isArray(defaultValue)
          ? defaultValue
          : [defaultValue];
      }
      return defaultValue;
    } else if (
      prefill &&
      field.name &&
      (profile[field.name] || user[field.name])
    ) {
      return user[field.name] || profile[field.name];
    }
    return type === ValueType.Text ? '' : [];
  };

  const setFields = (_fields: any[]) => {
    memberValues.value = {};
    const inputFieldTypes = [
      'select',
      'list',
      'text',
      'email',
      'password',
      'number',
      'phone',
      'freelibrary',
      'date',
      'boolean',
      'phone',
      'referralCode',
      'hidden',
    ];

    const inputFields = _fields.filter((field) =>
      inputFieldTypes.includes(field.type)
    );

    inputFields.forEach((field) => {
      memberValues.value[field.name] = {
        label: field.label,
        value: null,
      };
      switch (field.type) {
        case 'select':
          if (field.multiple) {
            const defaultValue = getDefaultValue(field, ValueType.Array);
            memberValues.value[field.name].value = defaultValue;
          } else {
            memberValues.value[field.name].value = getDefaultValue(field);
          }
          break;
        case 'list':
          memberValues.value[field.name].value = getDefaultValue(field);
          break;
        case 'date':
          let defaultValue = getDefaultValue(field); // eslint-disable-line
          defaultValue = defaultValue ? defaultValue.slice(0, 10) : '';
          memberValues.value[field.name].value = defaultValue;
          break;
        case 'email':
          {
            const defaultValue = getDefaultValue(field);
            memberValues.value[field.name].value = defaultValue;

            if (defaultValue) {
              if (field.name === 'confirmsIdentifier') {
                field.type = 'hidden';
              }
              field.readonly = true;
            }
          }
          break;
        case 'boolean':
          {
            const defaultValue = getDefaultValue(field);
            if (typeof defaultValue === 'boolean') {
              memberValues.value[field.name].value = defaultValue;
            } else if (typeof defaultValue === 'string') {
              if (defaultValue === 'true')
                memberValues.value[field.name].value = true;
              if (defaultValue === 'false')
                memberValues.value[field.name].value = false;
            } else {
              memberValues.value[field.name].value = '';
            }
          }
          break;
        default:
          memberValues.value[field.name].value = getDefaultValue(field);
          break;
      }
    });
  };

  const saveInitialValues = () => {
    initialValues.value = JSON.parse(JSON.stringify(memberValues.value));
  };

  const getValue = (name) => {
    return memberValues.value[name].value;
  };
  const setValue = (name, value) => {
    memberValues.value[name].value = value;
  };

  watchEffect(() => {
    if (step.value?.fields) {
      setFields(step.value?.fields);
      if (prefill && !initialValues.value) saveInitialValues();
      id.value = step.value?.id;
    }
  });

  watchEffect(() => {
    if (initialValues.value && prefill) {
      edited.value =
        Object.keys(memberValues.value).findIndex((key) => {
          return (
            memberValues.value[key].value !== initialValues.value[key].value
          );
        }) !== -1;
    }
  });

  return { memberValues, setFields, getValue, setValue, id, edited };
};
